/**
 * Curtain Calculator - Core Calculation Engine
 * Implements calculation logic for different header types and pricing
 */

class CurtainCalculator {
  constructor(configLoader) {
    this.configLoader = configLoader;
    this.lastCalculation = null;
  }

  /**
   * Calculate total cost for curtain customization
   * @param {Object} params - Calculation parameters
   * @param {number} params.widthInches - Width in inches
   * @param {number} params.lengthInches - Length in inches
   * @param {string} params.headerType - Header type ID
   * @param {string} params.fabricId - Fabric ID
   * @param {string} params.liningId - Lining ID (optional)
   * @param {boolean} params.heatSetting - Heat setting service
   * @param {number} params.tieCount - Number of tie backs
   * @param {number} params.curtainQuantity - Number of curtain panels
   * @returns {Object} Calculation result with breakdown
   */
  calculateTotal(params) {
    try {
      // Validate inputs
      const validation = this.validateInputs(params);
      if (!validation.isValid) {
        return {
          success: false,
          errors: validation.errors,
          total: 0
        };
      }
      // Get configuration data
      const headerConfig = this.configLoader.getHeaderType(params.headerType);
      const fabricConfig = this.configLoader.getFabric(params.fabricId);
      const liningConfig = params.liningId ? this.configLoader.getLiningOptions()[params.liningId] : null;
      const baseSettings = this.configLoader.getBaseSettings();
      
      // Get quantity (default to 1 if not specified)
      const quantity = params.curtainQuantity || 1;

      // Calculate material usage (per panel)
      const materialUsage = this.calculateMaterialUsage(
        params.widthInches,
        params.lengthInches,
        headerConfig,
        fabricConfig
      );

      // Calculate individual costs (per panel)
      const fabricCostPerPanel = this.calculateFabricCost(materialUsage.meters, fabricConfig, params.liningId);
      const headerCostPerPanel = headerConfig.fixed_fee;
      const liningCostPerPanel = 0; // No longer separate lining cost - included in fabric cost
      const heatSettingCostPerPanel = params.heatSetting ? this.calculateHeatSettingCost(materialUsage.meters, fabricConfig) : 0;
      
      // Tie cost calculation: tieCount should already represent total ties for all panels
      const tieCost = params.tieCount ? this.calculateTieCost(params.tieCount) : 0;

      // Calculate per-panel customization cost
      // const customizationCostPerPanel = fabricCostPerPanel + headerCostPerPanel + liningCostPerPanel + heatSettingCostPerPanel;
      
      //remove header cost price
      const customizationCostPerPanel = fabricCostPerPanel + liningCostPerPanel + heatSettingCostPerPanel;
      
      console.log('customizationCostPerPanel', customizationCostPerPanel);
      // Calculate total customization costs for all panels
      const totalCustomizationCost = (customizationCostPerPanel * quantity) + tieCost;
      
      // Get base product price (per panel)
      const baseProductPricePerPanel = this.getBaseProductPrice();
      
      console.log('baseProductPricePerPanel', baseProductPricePerPanel);
      console.log('headerCostPerPanel', headerCostPerPanel);
      // Calculate total base product price for all panels
      const totalBaseProductPrice = baseProductPricePerPanel * quantity;
      
      // Calculate final total: base product price + customization costs (both already multiplied by quantity)
      // const total = totalBaseProductPrice + (totalCustomizationCost * Shopify.currency.rate);
      const total = totalCustomizationCost * Shopify.currency.rate;

      // Calculate weight for all panels
      const weight = this.calculateWeight(params, quantity);

      // Store calculation for reference
      this.lastCalculation = {
        params,
        materialUsage,
        weight,
        quantity,
        breakdown: {
          // Per panel costs
          fabricCostPerPanel,
          headerCostPerPanel,
          liningCostPerPanel,
          heatSettingCostPerPanel,
          baseProductPricePerPanel,
          customizationCostPerPanel,
          
          // Total costs
          fabricCost: fabricCostPerPanel * quantity,
          headerCost: 0, // set header cost total 0
          liningCost: liningCostPerPanel * quantity,
          heatSettingCost: heatSettingCostPerPanel * quantity,
          tieCost,
          totalCustomizationCost,
          totalBaseProductPrice: 0, // set product base price 0
          total
        },
        timestamp: new Date()
      };

      return {
        success: true,
        total,
        weight,
        breakdown: this.lastCalculation.breakdown,
        materialUsage,
        currency: baseSettings.currency
      };

    } catch (error) {
      console.error('Calculation error:', error);
      return {
        success: false,
        errors: [`Calculation failed: ${error.message}`],
        total: 0
      };
    }
  }

/**
 * Calculate material usage based on header type
 * @param {number} widthInches - Width in inches
 * @param {number} lengthInches - Length in inches
 * @param {Object} headerConfig - Header configuration
 * @param {Object} fabricConfig - Fabric configuration (added for max_cm)
 * @returns {Object} Material usage details
 */
calculateMaterialUsage(widthInches, lengthInches, headerConfig, fabricConfig) {
  const widthCm = this.configLoader.inchesToCm(widthInches);
  const lengthCm = this.configLoader.inchesToCm(lengthInches);

  let materialWidth, materialHeight;

  switch (headerConfig.category) {
    case 'grommet':
      materialWidth = this.calculateGrommetWidth(widthCm, headerConfig.material_calculation);
      materialHeight = this.calculateGrommetHeight(lengthCm, headerConfig.material_calculation);
      break;

    case 'pleated':
      materialWidth = this.calculatePleatedWidth(widthCm, headerConfig.material_calculation);
      materialHeight = this.calculatePleatedHeight(lengthCm, headerConfig.material_calculation);
      break;

    case 'rod_tie':
      materialWidth = this.calculateRodTieWidth(widthCm, headerConfig.material_calculation);
      materialHeight = this.calculateRodTieHeight(lengthCm, headerConfig.material_calculation);
      break;

    default:
      throw new Error(`Unknown header category: ${headerConfig.category}`);
  }

  const maxCm = fabricConfig?.max_cm;

  let meters;

  if (maxCm && materialWidth > maxCm && materialHeight > maxCm) {
    // -----------------------------
    //  两边都超过最大门幅 → 拼幅计算
    // -----------------------------
    const panelCount = Math.ceil(materialWidth / maxCm);
    const totalCm = materialHeight * panelCount; 
    meters = parseFloat(this.configLoader.cmToMeters(totalCm).toFixed(3));

  } else {
    // -----------------------------
    // 否则 → 使用原本 Max 规则
    // -----------------------------
    const maxDimension = Math.max(materialWidth, materialHeight);
    meters = parseFloat(this.configLoader.cmToMeters(maxDimension).toFixed(3));
  }

  return {
    widthCm: materialWidth,
    heightCm: materialHeight,
    maxDimensionCm: Math.max(materialWidth, materialHeight),
    meters
  };
}


  /**
   * Calculate grommet header material width
   * @param {number} widthCm - Width in cm
   * @param {Object} calc - Calculation parameters
   * @returns {number} Material width in cm
   */
  calculateGrommetWidth(widthCm, calc) {
    return widthCm + calc.width_seam + calc.grommet_side_distance;
  }

  /**
   * Calculate grommet header material height
   * @param {number} lengthCm - Length in cm
   * @param {Object} calc - Calculation parameters
   * @returns {number} Material height in cm
   */
  calculateGrommetHeight(lengthCm, calc) {
    return lengthCm + calc.length_seam + calc.top_hem + calc.grommet_top_distance + calc.bottom_hem;
  }

  /**
   * Calculate pleated header material width
   * @param {number} widthCm - Width in cm
   * @param {Object} calc - Calculation parameters
   * @returns {number} Material width in cm
   */
  calculatePleatedWidth(widthCm, calc) {
    const pleatCount = Math.ceil(widthCm / calc.pleat_spacing) + 1;
    const pleatMaterial = calc.pleat_amount * pleatCount;
    return widthCm + calc.width_seam + calc.pleat_side_distance + pleatMaterial;
  }

  /**
   * Calculate pleated header material height
   * @param {number} lengthCm - Length in cm
   * @param {Object} calc - Calculation parameters
   * @returns {number} Material height in cm
   */
  calculatePleatedHeight(lengthCm, calc) {
    return lengthCm + calc.length_seam + calc.bottom_hem;
  }

  /**
   * Calculate rod and tie header material width
   * @param {number} widthCm - Width in cm
   * @param {Object} calc - Calculation parameters
   * @returns {number} Material width in cm
   */
  calculateRodTieWidth(widthCm, calc) {
    return widthCm + calc.width_seam + calc.tie_length;
  }

  /**
   * Calculate rod and tie header material height
   * @param {number} lengthCm - Length in cm
   * @param {Object} calc - Calculation parameters
   * @returns {number} Material height in cm
   */
  calculateRodTieHeight(lengthCm, calc) {
    return lengthCm + calc.length_seam + calc.top_hem + calc.top_distance + calc.bottom_hem;
  }

  /**
   * Calculate fabric cost using combination pricing with lining
   * @param {number} meters - Material usage in meters
   * @param {Object} fabricConfig - Fabric configuration
   * @param {string} liningId - Lining ID to determine pricing
   * @returns {number} Fabric cost (including lining)
   */
  calculateFabricCost(meters, fabricConfig, liningId) {
    let pricePerMeter;

    // Determine price based on lining selection
    switch (liningId) {
      case 'privacy_lining':
        pricePerMeter = fabricConfig.price_with_privacy_lining;
        break;
      case 'blackout_lining':
        pricePerMeter = fabricConfig.price_with_blackout_lining;
        break;
      case 'room_darking_lining':
        pricePerMeter = fabricConfig.price_with_room_darking_lining;
        break;
      case 'unlined':
      default:
        pricePerMeter = fabricConfig.price_per_meter;
        break;
    }

    // If lining is not supported by fabric, use base price
    if (!fabricConfig.supports_lining && liningId !== 'unlined') {
      pricePerMeter = fabricConfig.price_per_meter;
    }

    return parseFloat((meters * pricePerMeter).toFixed(2));
  }

  /**
   * Calculate lining cost (now always returns 0 as lining is included in fabric cost)
   * @param {number} meters - Material usage in meters
   * @param {Object} liningConfig - Lining configuration
   * @returns {number} Lining cost (always 0)
   */
  calculateLiningCost(meters, liningConfig) {
    // Lining cost is now included in fabric combination pricing
    return 0;
  }

  /**
   * Calculate heat setting cost
   * @param {number} meters - Material usage in meters
   * @param {Object} fabricConfig - Fabric configuration
   * @returns {number} Heat setting cost
   */
  calculateHeatSettingCost(meters, fabricConfig) {
    if (!fabricConfig.supports_heat_setting) {
      return 0;
    }
    
    const services = this.configLoader.getAdditionalServices();
    const heatSettingService = services.heat_setting;
    
    if (!heatSettingService || !heatSettingService.enabled) {
      return 0;
    }

    return parseFloat((meters * heatSettingService.price_per_meter).toFixed(2));
  }

  /**
   * Calculate tie cost
   * @param {number} tieCount - Number of curtain panels (not individual ties)
   * @returns {number} Tie cost
   */
  calculateTieCost(tieCount) {
    const services = this.configLoader.getAdditionalServices();
    const tieService = services.ties;
    
    if (!tieService || !tieService.enabled) {
      return 0;
    }

    return parseFloat((tieCount * tieService.price_per_piece).toFixed(2));
  }

  /**
   * Get base product price from current product
   * @returns {number} Base product price
   */
  getBaseProductPrice() {
    try {
      // Check if product pricing is available
      if (window.currentProductPricing && window.currentProductPricing.basePriceDecimal) {
        const productPrice = parseFloat(window.currentProductPricing.basePriceDecimal);
        
        // Use product price as base price if it's valid
        if (!isNaN(productPrice) && productPrice > 0) {
          return productPrice;
        }
      }

      // If no valid product price is available, throw an error
      throw new Error('Product price not available - ensure product has valid pricing');
    } catch (error) {
      console.error('Error getting base product price:', error);
      return 0;
    }
  }

  /**
   * Validate calculation inputs
   * @param {Object} params - Input parameters
   * @returns {Object} Validation result
   */
  validateInputs(params) {
    const errors = [];

    if (!params.widthInches || !params.lengthInches) {
      errors.push('Width and length are required');
    } else {
      if (params.widthInches <= 0) {
        errors.push('Width must be greater than 0');
      } else if (params.widthInches < 12) {
        errors.push('Width must be at least 12 inches');
      }
      
      if (params.lengthInches <= 0) {
        errors.push('Length must be greater than 0');
      } else if (params.lengthInches < 12) {
        errors.push('Length must be at least 12 inches');
      }
    }

    // Validate header type
    if (!params.headerType) {
      errors.push('Header type is required');
    } else {
      const headerConfig = this.configLoader.getHeaderType(params.headerType);
      if (!headerConfig) {
        errors.push('Invalid header type');
      } else if (!headerConfig.enabled) {
        errors.push('Selected header type is not available');
      }
    }

    // Validate fabric
    if (!params.fabricId) {
      errors.push('Fabric selection is required');
    } else {
      const fabricConfig = this.configLoader.getFabric(params.fabricId);
      if (!fabricConfig) {
        errors.push('Invalid fabric selection');
      }
    }

    // Validate lining if provided
    if (params.liningId && params.liningId !== 'unlined') {
      const fabricConfig = this.configLoader.getFabric(params.fabricId);
      const liningOptions = this.configLoader.getLiningOptions();
      const liningConfig = liningOptions[params.liningId];
      
      if (!liningConfig) {
        errors.push('Invalid lining selection');
      } else if (!liningConfig.enabled) {
        errors.push('Selected lining is not available');
      } else if (fabricConfig && !fabricConfig.supports_lining) {
        errors.push('Selected fabric does not support lining options');
      }
    }

    // Validate heat setting compatibility
    if (params.heatSetting && params.fabricId) {
      const fabricConfig = this.configLoader.getFabric(params.fabricId);
      if (fabricConfig && !fabricConfig.supports_heat_setting) {
        errors.push('Selected fabric does not support heat setting');
      }
    }

    // Validate tie count
    if (params.tieCount && (params.tieCount < 0 || params.tieCount > 20)) {
      errors.push('Invalid tie count (must be between 0 and 20)');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get the last calculation result
   * @returns {Object|null} Last calculation or null
   */
  getLastCalculation() {
    return this.lastCalculation;
  }

  /**
   * Format price for display
   * @param {number} amount - Price amount
   * @returns {string} Formatted price string
   */
  formatPrice(amount) {
    return formatMoney(amount * 100);
    const symbol = this.configLoader.getCurrencySymbol();
    const decimals = this.configLoader.getDecimalPlaces();
    return `${symbol}${amount.toFixed(decimals)}`;
  }

  /**
   * Get calculation breakdown for display
   * @returns {Object|null} Formatted breakdown or null
   */
  getFormattedBreakdown() {
    if (!this.lastCalculation) {
      return null;
    }

    const breakdown = this.lastCalculation.breakdown;
    const format = (amount) => this.formatPrice(amount);

    return {
      fabricCost: format(breakdown.fabricCost),
      headerCost: format(breakdown.headerCost),
      liningCost: format(breakdown.liningCost),
      heatSettingCost: format(breakdown.heatSettingCost),
      tieCost: format(breakdown.tieCost),
      customizationSubtotal: format(breakdown.customizationSubtotal),
      baseProductPrice: format(breakdown.totalBaseProductPrice),
      total: format(breakdown.total),
      materialUsage: `${this.lastCalculation.materialUsage.meters}m`,
      weight: `${(this.lastCalculation.weight * 2.20462).toFixed(2)}lb`
    };
  }

  /**
   * Get current base product price information
   * @returns {Object} Base product price information
   */
  getBaseProductPriceInfo() {
    const baseProductPrice = this.getBaseProductPrice();
    
    return {
      baseProductPrice: baseProductPrice,
      priceDecimal: baseProductPrice, // Keep for backward compatibility
      isUsingProductPrice: baseProductPrice > 0,
      productTitle: window.currentProductPricing?.productTitle || 'Current Product',
      formattedBasePrice: this.formatPrice(baseProductPrice)
    };
  }

  /**
   * Calculate product weight in kilograms
   * @param {Object} params - Calculation parameters
   * @param {number} curtainCount - Number of curtain panels
   * @returns {number} Total weight in kilograms
   */
  calculateWeight(params, curtainCount = 1) {
    try {
      // Get configuration data
      const headerConfig = this.configLoader.getHeaderType(params.headerType);
      const fabricConfig = this.configLoader.getFabric(params.fabricId);
      const liningConfig = params.liningId ? this.configLoader.getLiningOptions()[params.liningId] : null;

      // Calculate material usage
      const materialUsage = this.calculateMaterialUsage(
        params.widthInches,
        params.lengthInches,
        headerConfig,
        fabricConfig
      );

      // Calculate area in square meters
      const lengthMeters = materialUsage.heightCm / 100;
      const widthMeters = materialUsage.widthCm / 100;
      const areaSquareMeters = lengthMeters * widthMeters;

      // Calculate total GSM (fabric + lining)
      let totalGsm = fabricConfig.weight_gsm;
      if (liningConfig && params.liningId !== 'unlined') {
        totalGsm += liningConfig.weight_gsm;
      }

      // Calculate weight: area × GSM × panels ÷ 1000 (to convert grams to kg)
      const totalWeightKg = (areaSquareMeters * totalGsm * curtainCount) / 1000;

      return parseFloat(totalWeightKg.toFixed(3));
    } catch (error) {
      console.error('Weight calculation error:', error);
      return 0;
    }
  }
}

// Create global instance
window.curtainCalculator = null;

// Initialize when config is loaded
document.addEventListener('DOMContentLoaded', async function() {
  try {
    const configLoader = await window.curtainConfigLoader.getInitializationPromise();
    console.log(configLoader);
    window.curtainCalculator = new CurtainCalculator(configLoader);
  } catch (error) {
    console.error('Failed to initialize curtain calculator:', error);
  }
});

if (typeof module !== 'undefined' && module.exports) {
  module.exports = CurtainCalculator;
} 